import { computed, inject } from '@angular/core';

import { updateState, withDevtools, withDevToolsStub, withGlitchTracking } from '@angular-architects/ngrx-toolkit';
import { signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';

import { ConfigService } from '../services/config/config.service';
import { InsurancePolicyService } from '../services/insurance/insurance-policy.service';
import { FarmStore } from './farm.store';
import { NotificationStore } from './notification.store';
import { SuccessNotification } from '../models/notifications/app-notification';
import { InsurancePolicy } from '../entities/insurance/insurance-policy.entities';
import {
  InsurancePolicyQueryParams,
  InsurancePolicyFilters,
  InsurancePolicyCreateRequest,
  InsurancePolicyUpdateRequest,
  InsurancePolicyStatistics
} from '../entities/insurance/insurance-policy-api.types';
import { EntityCreatedResponse } from '../models/api/api-response.model';
import { withCondition } from './utils/with-condition';
import { upsertMany } from '../helpers/data.helpers';

interface InsurancePolicyState {
  policies: InsurancePolicy[];
  selectedPolicy: InsurancePolicy | null;
  loading: boolean;
  error: string | null;
  totalPolicies: number;
  currentPage: number;
  pageSize: number;
  hasMorePolicies: boolean;
  searchQuery: string;
  filters: InsurancePolicyFilters;
  policyStatistics: InsurancePolicyStatistics | null;
}

const initialState: InsurancePolicyState = {
  policies: [],
  selectedPolicy: null,
  loading: false,
  error: null,
  totalPolicies: 0,
  currentPage: 0,
  pageSize: 100,
  hasMorePolicies: false,
  searchQuery: '',
  filters: {},
  policyStatistics: null,
};

export const InsurancePolicyStore = signalStore(
  withState(initialState),
  withProps(() => ({
    configService: inject(ConfigService),
    policyService: inject(InsurancePolicyService),
    farmStore: inject(FarmStore),
    notificationStore: inject(NotificationStore),
  })),
  withComputed(({ policies, selectedPolicy, loading, error, totalPolicies, hasMorePolicies, policyStatistics }) => ({
    hasPolicies: computed(() => policies().length > 0),
    policiesCount: computed(() => policies().length),
    totalPoliciesCount: computed(() => totalPolicies()),
    isLoading: computed(() => loading()),
    hasError: computed(() => error() !== null),
    selectedPolicyName: computed(() => selectedPolicy()?.policy_name || null),
    hasMorePoliciesAvailable: computed(() => hasMorePolicies()),
    hasPolicyStatistics: computed(() => policyStatistics() !== null),
  })),
  withCondition(({ configService }) => !configService?.environment?.production,
    withDevtools('insurance-policy', withGlitchTracking()),
    withDevToolsStub('insurance-policy'),
  ),
  withMethods((store) => ({
    loadPolicies(params?: InsurancePolicyQueryParams, filters?: InsurancePolicyFilters, append = false): void {
      updateState(store, '[InsurancePolicy] Load Policies Start', { loading: true, error: null });

      // Automatically include current farm ID in filters
      const currentFarmId = store.farmStore.currentFarm()?.id;
      const farmFilters = {
        ...filters,
        ...(currentFarmId && { farm_id: currentFarmId })
      };

      store.policyService.getPolicies(params, farmFilters).subscribe({
        next: (response) => {
          const newPolicies = append ? upsertMany(store.policies(), response.data) : response.data;
          updateState(store, '[InsurancePolicy] Load Policies Success', {
            policies: newPolicies,
            totalPolicies: response.total,
            currentPage: params?.skip ? Math.floor((params.skip + response.data.length) / (params?.limit || 100)) : 0,
            pageSize: params?.limit || 100,
            hasMorePolicies: (params?.skip || 0) + response.data.length < response.total,
            loading: false,
            error: null,
            filters: filters || {},
            searchQuery: ''
          });
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Load Policies Error', {
            loading: false,
            error: error.message || 'Failed to load policies'
          });
        }
      });
    },

    searchPolicies(query: string, params?: Omit<InsurancePolicyQueryParams, 'q'>): void {
      updateState(store, '[InsurancePolicy] Search Policies Start', { loading: true, error: null, searchQuery: query });

      const currentFarmId = store.farmStore.currentFarm()?.id;
      const searchParams = { ...params, q: query };
      const farmFilters = {
        ...(currentFarmId && { farm_id: currentFarmId })
      };

      store.policyService.getPolicies(searchParams, farmFilters).subscribe({
        next: (response) => {
          const mergedPolicies = upsertMany(store.policies(), response.data);
          updateState(store, '[InsurancePolicy] Search Policies Success', {
            policies: mergedPolicies,
            loading: false,
            error: null,
            searchQuery: query
          });
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Search Policies Error', {
            loading: false,
            error: error.message || 'Failed to search policies'
          });
        }
      });
    },

    loadPolicyById(id: string): void {
      updateState(store, '[InsurancePolicy] Load Policy By ID Start', { loading: true, error: null });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Insurance Policy] Load Policy By ID Error', {
          loading: false,
          error: 'Farm ID is required to get policy'
        });
        return;
      }

      store.policyService.getPolicyById(id, farmId).subscribe({
        next: (policy) => {
          const existingIndex = store.policies().findIndex(p => p.id === id);
          const newPolicies = [...store.policies()];
          if (existingIndex >= 0) {
            newPolicies[existingIndex] = policy;
          } else {
            newPolicies.push(policy);
          }

          updateState(store, '[InsurancePolicy] Load Policy By ID Success', {
            policies: newPolicies,
            loading: false,
            error: null
          });
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Load Policy By ID Error', {
            loading: false,
            error: error.message || 'Failed to load policy'
          });
        }
      });
    },

    createPolicy(policyData: InsurancePolicyCreateRequest): void {
      updateState(store, '[InsurancePolicy] Create Policy Start', { loading: true, error: null });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Insurance Policy] Create Policy Error', {
          loading: false,
          error: 'Farm ID is required to create policy'
        });
        return;
      }

      store.policyService.createPolicy(policyData, farmId).subscribe({
        next: (response: EntityCreatedResponse) => {
          const newPolicy: InsurancePolicy = {
            ...policyData,
            id: response.id,
            farm_id: farmId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          updateState(store, '[InsurancePolicy] Create Policy Success', {
            policies: [...store.policies(), newPolicy],
            totalPolicies: store.totalPolicies() + 1,
            loading: false,
            error: null
          });

          store.notificationStore.addNotification(new SuccessNotification({
            summary: 'notifications.success',
            detail: 'insurance.policies.notifications.created'
          }));
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Create Policy Error', {
            loading: false,
            error: error.message || 'Failed to create policy'
          });
        }
      });
    },

    updatePolicy(id: string, updates: InsurancePolicyUpdateRequest): void {
      updateState(store, '[InsurancePolicy] Update Policy Start', { loading: true, error: null });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Insurance Policy] Update Policy Error', {
          loading: false,
          error: 'Farm ID is required to update policy'
        });
        return;
      }

      store.policyService.patchPolicy(id, updates, farmId).subscribe({
        next: () => {
          const currentPolicy = store.policies().find(p => p.id === id);
          if (!currentPolicy) {
            updateState(store, '[InsurancePolicy] Update Policy Error', {
              loading: false,
              error: 'Policy not found in store after update'
            });
            return;
          }

          const updatedPolicy: InsurancePolicy = {
            ...currentPolicy,
            ...updates,
            updated_at: new Date().toISOString()
          };

          const newPolicies = store.policies().map(policy =>
            policy.id === id ? updatedPolicy : policy
          );

          updateState(store, '[InsurancePolicy] Update Policy Success', {
            policies: newPolicies,
            loading: false,
            error: null
          });

          if (store.selectedPolicy()?.id === id) {
            updateState(store, '[InsurancePolicy] Update Selected Policy', { selectedPolicy: updatedPolicy });
          }

          store.notificationStore.addNotification(new SuccessNotification({
            summary: 'notifications.success',
            detail: 'insurance.policies.notifications.updated'
          }));
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Update Policy Error', {
            loading: false,
            error: error.message || 'Failed to update policy'
          });
        }
      });
    },

    deletePolicy(id: string): void {
      updateState(store, '[InsurancePolicy] Delete Policy Start', { loading: true, error: null });

      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        updateState(store, '[Insurance Policy] Delete Policy Error', {
          loading: false,
          error: 'Farm ID is required to delete policy'
        });
        return;
      }

      store.policyService.deletePolicy(id, farmId).subscribe({
        next: () => {
          const newPolicies = store.policies().filter(policy => policy.id !== id);
          updateState(store, '[InsurancePolicy] Delete Policy Success', {
            policies: newPolicies,
            totalPolicies: Math.max(0, store.totalPolicies() - 1),
            loading: false,
            error: null
          });

          // Clear selected policy if it's the one being deleted
          if (store.selectedPolicy()?.id === id) {
            updateState(store, '[InsurancePolicy] Clear Selected Policy', { selectedPolicy: null });
          }
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Delete Policy Error', {
            loading: false,
            error: error.message || 'Failed to delete policy'
          });
        }
      });
    },

    setPoliciesData(policies: InsurancePolicy[]): void {
      updateState(store, '[InsurancePolicy] Set Policies Data', { policies, loading: false, error: null });
    },

    setLoading(loading: boolean): void {
      updateState(store, '[InsurancePolicy] Set Loading', { loading });
    },

    selectPolicy(policy: InsurancePolicy | null): void {
      updateState(store, '[InsurancePolicy] Select Policy', { selectedPolicy: policy });
    },

    selectPolicyById(policyId: string): void {
      const policy = store.policies().find(p => p.id === policyId) || null;
      this.selectPolicy(policy);
    },

    loadNextPage(): void {
      if (!store.hasMorePolicies()) return;

      const skip = store.currentPage() * store.pageSize();
      const params: InsurancePolicyQueryParams = {
        skip,
        limit: store.pageSize()
      };

      this.loadPolicies(params, store.filters(), true);
    },

    setFilters(filters: InsurancePolicyFilters): void {
      updateState(store, '[InsurancePolicy] Set Filters', { filters });
      this.loadPolicies(undefined, filters);
    },

    clearFilters(): void {
      updateState(store, '[InsurancePolicy] Clear Filters', { filters: {}, searchQuery: '' });
      this.loadPolicies();
    },

    refreshPolicies(): void {
      this.loadPolicies();
    },

    clearError(): void {
      updateState(store, '[InsurancePolicy] Clear Error', { error: null });
    },

    reset(): void {
      updateState(store, '[InsurancePolicy] Reset', initialState);
    },

    initializePolicies(): void {
      // Load policies for the current farm
      this.loadPolicies();
    },

    getPolicyById(id: string): InsurancePolicy | undefined {
      return store.policies().find(policy => policy.id === id);
    },

    hasAccessToPolicy(policyId: string): boolean {
      return store.policies().some(policy => policy.id === policyId);
    },

    loadPolicyStatistics(farmId: string): void {
      updateState(store, '[InsurancePolicy] Load Policy Statistics Start', { loading: true, error: null });

      store.policyService.getPolicyStatistics(farmId).subscribe({
        next: (statistics) => {
          updateState(store, '[InsurancePolicy] Load Policy Statistics Success', {
            policyStatistics: statistics,
            loading: false,
            error: null
          });
        },
        error: (error) => {
          updateState(store, '[InsurancePolicy] Load Policy Statistics Error', {
            loading: false,
            error: error.message || 'Failed to load policy statistics'
          });
        }
      });
    }
  }))
);
